import { useEffect, useState } from "react";
import { productName } from "./productName";
import {
  type Account,
  ApiError,
  api,
  formatMoney,
  type Location,
  type Lot,
  type Movement,
  type Product,
  type SlabDetails,
} from "./api";
import { singaporeToday } from "./dates";
import { DateField, SelectField } from "./fields";
import { fileForReading, ReadFileButton, unsureSentence, useReading } from "./reading";
import { Alert, Badge, Field, Loading, Offcanvas, PanelSection, Table, Tabs, Th, TwoLine, Working } from "./ui";
import { cropToSlab } from "./attachments";
import { type CertBox, CertMarker, boxInCrop, boxWithin, cutPhoto } from "./certCover";

const MOVEMENT_LABELS: Record<string, string> = {
  in: "Inventory received",
  sale: "Sold",
  buyback_in: "Customer buyback",
  write_off: "Written off",
  adjust: "Count adjustment",
  transfer_out: "Transferred out",
  transfer_in: "Transferred in",
  convert_out: "Opened into packs",
  convert_in: "Opened from a box",
  sell_to_company_out: "Sold to the company",
  sell_to_company_in: "Purchased from staff",
};

type Action = "count" | "move" | "open" | "writeOff" | "slab" | "sell";

/** A whole number typed in a box, or undefined when it is not one. */
function wholeNumber(text: string): number | undefined {
  const value = Number(text.trim());
  return text.trim() !== "" && Number.isInteger(value) && value > 0 ? value : undefined;
}

/**
 * One batch in the template's right-hand panel: what it is, what can be done with it,
 * and everything that has happened to it.
 *
 * Only what applies is offered. A slab has its details, a box can be opened, stock held
 * personally can be sold to the company, and each needs the role OHC set for it.
 */
export function StockPanel({
  lotId,
  seesCost,
  canManage,
  canAdjust,
  canBuyFromStaff,
  products,
  locations,
  onChanged,
  onClose,
}: {
  readonly lotId: string;
  readonly seesCost: boolean;
  readonly canManage: boolean;
  readonly canAdjust: boolean;
  readonly canBuyFromStaff: boolean;
  readonly products: readonly Product[];
  readonly locations: readonly Location[];
  readonly onChanged: () => void;
  readonly onClose: () => void;
}): React.ReactElement {
  const [lot, setLot] = useState<Lot | undefined>();
  const [movements, setMovements] = useState<Movement[]>([]);
  const [action, setAction] = useState<Action>("count");
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();

  async function load(): Promise<void> {
    try {
      const result = await api.lot(lotId);
      setLot(result.lot);
      setMovements(result.movements);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load this batch");
    }
  }

  useEffect(() => {
    void load();
  }, [lotId]);

  const product = lot?.productId ? products.find((entry) => entry.id === lot.productId) : undefined;
  // Anything that holds other sealed things: a case its boxes, a box its packs (D, 12 October 2026).
  const boxLike = product ? ["box", "etb", "bundle"].includes(product.itemType) || Boolean(product.containsProductId) : false;

  const actions: { value: Action; label: string }[] = [{ value: "count", label: "Count" }];
  if (lot && lot.quantityOnHand > 0) {
    // Both need stock.move on the server, which starts at supervisor.
    if (canManage) actions.push({ value: "move", label: "Transfer" });
    if (canManage && boxLike) actions.push({ value: "open", label: product?.containsProductId ? "Open or split" : "Convert to packs" });
    if (canAdjust) actions.push({ value: "writeOff", label: "Write off" });
    if (lot.ownerKind === "person" && canBuyFromStaff) actions.push({ value: "sell", label: "Sell to company" });
  }
  if (lot?.kind === "slab" && canManage) actions.push({ value: "slab", label: "Graded card details" });
  const current = actions.some((entry) => entry.value === action) ? action : "count";

  /** After any change: say what happened, and reload this batch and the list behind it. */
  function done(message: string): void {
    setNotice(message);
    setError(undefined);
    void load();
    onChanged();
  }

  const title = lot ? (lot.kind === "slab" ? productName({ name: lot.slabName ?? "Graded card", language: lot.slabLanguage }) : productName({ name: lot.productName ?? "Inventory", language: product?.language })) : "Inventory";

  return (
    <Offcanvas wide title={title} onClose={onClose}>
      {!lot ? (
        <PanelSection last>{error ? <Alert tone="danger">{error}</Alert> : <Loading />}</PanelSection>
      ) : (
        <>
          <PanelSection>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem] mb-1">Quantity on hand</p>
                <p className={`font-semibold text-[1.5rem] mb-0 ${lot.quantityOnHand < 0 ? "text-danger" : ""}`}>
                  {lot.quantityOnHand}
                </p>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                {lot.purchaseBatchRef ? <Badge tone="light">Batch {lot.purchaseBatchRef}</Badge> : null}
                <Badge tone={lot.ownerKind === "company" ? "primary" : "secondary"}>{lot.ownerName}</Badge>
                {lot.locationName ? <Badge tone="light">{lot.locationName}</Badge> : null}
                {!lot.isComplete ? <Badge tone="warning">Incomplete</Badge> : null}
              </div>
            </div>
          </PanelSection>

          <PanelSection>
            <Tabs tabs={actions} value={current} onChange={(value) => {
              setAction(value);
              setNotice(undefined);
              setError(undefined);
            }} />
            <div className="mt-4">
              {notice ? (
                <Alert tone="success" onClose={() => setNotice(undefined)}>
                  {notice}
                </Alert>
              ) : null}
              {error ? <Alert tone="danger">{error}</Alert> : null}
              {current === "count" ? <CountForm lot={lot} onError={setError} onDone={done} /> : null}
              {current === "move" ? <MoveForm lot={lot} locations={locations} onError={setError} onDone={done} /> : null}
              {current === "open" && product ? (
                <OpenForm lot={lot} box={product} products={products} onError={setError} onDone={done} />
              ) : null}
              {current === "writeOff" ? <WriteOffForm lot={lot} onError={setError} onDone={done} /> : null}
              {current === "sell" ? (
                <SellForm lot={lot} locations={locations} seesCost={seesCost} onError={setError} onDone={done} />
              ) : null}
              {current === "slab" ? <SlabForm lot={lot} onError={setError} onDone={done} /> : null}
            </div>
          </PanelSection>

          <PanelSection title="History" last>
            <Table
              head={
                <>
                  <Th>Date</Th>
                  <Th>Transaction</Th>
                  <Th end>Units</Th>
                  {seesCost ? <Th end>Cost</Th> : null}
                  <Th>Recorded by</Th>
                </>
              }
            >
              {movements.map((movement) => (
                <tr key={movement.id} className="border-b border-defaultborder">
                  <td className="text-[#8c9097] dark:text-white/50">{movement.happenedOn}</td>
                  <td>
                    <TwoLine main={MOVEMENT_LABELS[movement.kind] ?? movement.kind} sub={movement.reason ?? undefined} />
                  </td>
                  <td className={`!text-end font-semibold ${movement.quantity < 0 ? "text-danger" : "text-success"}`}>
                    {movement.quantity > 0 ? `+${movement.quantity}` : movement.quantity}
                  </td>
                  {seesCost ? <td className="!text-end">{formatMoney(movement.costCents ?? 0)}</td> : null}
                  <td>
                    {movement.actorName ?? ""}
                    {movement.deviceCode ? (
                      <span className="text-[#8c9097] dark:text-white/50"> on {movement.deviceCode}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </Table>
          </PanelSection>
        </>
      )}
    </Offcanvas>
  );
}

interface FormProps {
  readonly lot: Lot;
  readonly onError: (message: string) => void;
  readonly onDone: (message: string) => void;
}

/** Runs one action, turning a refusal into the sentence the server gave. */
function useAction(onError: (message: string) => void): {
  readonly busy: boolean;
  readonly run: (work: () => Promise<void>) => Promise<void>;
} {
  const [busy, setBusy] = useState(false);
  return {
    busy,
    run: async (work) => {
      setBusy(true);
      try {
        await work();
      } catch (problem) {
        onError(problem instanceof ApiError ? problem.message : "The action could not be completed");
      } finally {
        setBusy(false);
      }
    },
  };
}

function SubmitButton({ busy, disabled, children }: { readonly busy: boolean; readonly disabled: boolean; readonly children: string }) {
  return (
    <button type="submit" className="ti-btn ti-btn-primary-full !mb-0" disabled={busy || disabled}>
      {busy ? <Working label="Saving" /> : children}
    </button>
  );
}

function CountForm({ lot, onError, onDone }: FormProps): React.ReactElement {
  const [counted, setCounted] = useState(String(lot.quantityOnHand));
  const [reason, setReason] = useState("");
  const { busy, run } = useAction(onError);
  const quantity = Number(counted);
  const difference = Number.isFinite(quantity) ? quantity - lot.quantityOnHand : 0;

  return (
    <form
      className="grid grid-cols-12 gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!Number.isFinite(quantity) || quantity < 0) return onError("Enter the quantity counted");
        void run(async () => {
          await api.countLot(lot.id, quantity, singaporeToday(), reason);
          setReason("");
          onDone(`Count saved: ${quantity} on hand. ${Math.abs(difference)} ${difference < 0 ? "missing" : "surplus"} recorded.`);
        });
      }}
    >
      <Field
        id="counted"
        label="Quantity counted"
        className="sm:col-span-4 col-span-12"
        hint={
          <>
            System quantity: {lot.quantityOnHand}.
            {difference !== 0 ? (
              <span className={difference < 0 ? "text-danger" : "text-success"}>
                {" "}
                {Math.abs(difference)} {difference < 0 ? "missing" : "surplus"}.
              </span>
            ) : null}
          </>
        }
      >
        <input id="counted" type="text" inputMode="numeric" className="form-control" value={counted} onChange={(e) => setCounted(e.target.value)} />
      </Field>
      <Field id="count-reason" label="Reason for difference" className="sm:col-span-8 col-span-12">
        <input
          id="count-reason"
          type="text"
          className="form-control"
          value={reason}
          placeholder="End-of-day count, two packs short"
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      <div className="col-span-12">
        <SubmitButton busy={busy} disabled={difference === 0 || !reason.trim()}>
          Save count
        </SubmitButton>
      </div>
    </form>
  );
}

function MoveForm({ lot, locations, onError, onDone }: FormProps & { readonly locations: readonly Location[] }): React.ReactElement {
  const places = locations.filter((location) => location.isActive && location.id !== lot.locationId);
  const [to, setTo] = useState("");
  const [quantity, setQuantity] = useState(String(lot.quantityOnHand));
  const [on, setOn] = useState(singaporeToday());
  const [reason, setReason] = useState("");
  const { busy, run } = useAction(onError);
  const count = wholeNumber(quantity);

  if (places.length === 0) {
    return <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-0">Add another location first, using Add location above the inventory list.</p>;
  }
  return (
    <form
      className="grid grid-cols-12 gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!count) return onError("Enter the quantity to transfer");
        void run(async () => {
          await api.moveLot(lot.id, { quantity: count, toLocationId: to, happenedOn: on, ...(reason.trim() ? { reason } : {}) });
          const place = places.find((entry) => entry.id === to)?.name ?? "the selected location";
          onDone(`${count} transferred to ${place}.`);
        });
      }}
    >
      <Field id="move-to" label="Transfer to" className="sm:col-span-7 col-span-12">
        <SelectField
          id="move-to"
          value={to}
          placeholder="Select a location"
          options={places.map((place) => ({ value: place.id, label: place.name }))}
          onChange={setTo}
        />
      </Field>
      <Field id="move-quantity" label="Quantity" className="sm:col-span-5 col-span-12" hint={`Of ${lot.quantityOnHand} at this location.`}>
        <input id="move-quantity" type="text" inputMode="numeric" className="form-control" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
      </Field>
      <Field id="move-on" label="Date" className="sm:col-span-5 col-span-12">
        <DateField id="move-on" value={on} onChange={setOn} />
      </Field>
      <Field id="move-reason" label="Note" className="sm:col-span-7 col-span-12" hint="Optional.">
        <input id="move-reason" type="text" className="form-control" value={reason} placeholder="For the Suntec event" onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div className="col-span-12">
        <SubmitButton busy={busy} disabled={!to || !count}>
          Transfer
        </SubmitButton>
      </div>
    </form>
  );
}

function OpenForm({
  lot,
  box,
  products,
  onError,
  onDone,
}: FormProps & { readonly box: Product; readonly products: readonly Product[] }): React.ReactElement {
  // What it holds comes first: a case opens into its boxes, and the packs stay in the boxes.
  const inside = products.find((entry) => entry.id === box.containsProductId && entry.isActive);
  const packs = [...(inside ? [inside] : []), ...products.filter((entry) => entry.itemType === "pack" && entry.isActive && entry.id !== inside?.id)];
  // Failing that, the pack of the same set and language is almost always the one.
  const likely = inside ?? packs.find((entry) => entry.setName && entry.setName === box.setName && entry.language === box.language);
  const [packId, setPackId] = useState(likely?.id ?? "");
  /** How many of the chosen product one unit gives: what it holds, or its packs. */
  const each = packId === inside?.id ? (box.containsQuantity ?? 0) : (box.packsPerBox ?? 0);
  const target = products.find((entry) => entry.id === packId);
  const [boxes, setBoxes] = useState("1");
  const [on, setOn] = useState(singaporeToday());
  const { busy, run } = useAction(onError);
  const count = wholeNumber(boxes);

  if (!box.packsPerBox && !inside) {
    return (
      <Alert tone="warning">
        The number of packs in {box.name} is not set. Add Packs per unit to the product first, in the product catalogue
        below.
      </Alert>
    );
  }
  if (packs.length === 0) {
    return <Alert tone="warning">No pack product exists to convert into yet. Add it first using Add product.</Alert>;
  }
  return (
    <form
      className="grid grid-cols-12 gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!count) return onError("Enter the number of boxes to open");
        void run(async () => {
          if (!each) return onError(`Set how many packs are in one ${box.name} on the product first`);
          await api.openBoxes(lot.id, { boxes: count, packProductId: packId, happenedOn: on });
          onDone(`${count} x ${box.name} opened into ${count * each} x ${target?.name ?? "packs"}. The cost went with them.`);
        });
      }}
    >
      <Field id="open-boxes" label="How many to open" className="sm:col-span-4 col-span-12" hint={each ? `Each gives ${each} x ${target?.name ?? "packs"}.` : "Pick what it opens into."}>
        <input id="open-boxes" type="text" inputMode="numeric" className="form-control" value={boxes} onChange={(e) => setBoxes(e.target.value)} />
      </Field>
      <Field id="open-into" label="Opens into" className="sm:col-span-8 col-span-12">
        <SelectField
          id="open-into"
          value={packId}
          placeholder="Select the pack product"
          options={packs.map((entry) => ({ value: entry.id, label: `${entry.code} ${productName(entry)}` }))}
          onChange={setPackId}
        />
      </Field>
      <Field id="open-on" label="Date" className="sm:col-span-5 col-span-12">
        <DateField id="open-on" value={on} onChange={setOn} />
      </Field>
      <div className="col-span-12">
        <SubmitButton busy={busy} disabled={!packId || !count}>
          Convert to packs
        </SubmitButton>
      </div>
    </form>
  );
}

function WriteOffForm({ lot, onError, onDone }: FormProps): React.ReactElement {
  const [quantity, setQuantity] = useState("1");
  const [on, setOn] = useState(singaporeToday());
  const [reason, setReason] = useState("");
  const { busy, run } = useAction(onError);
  const count = wholeNumber(quantity);

  return (
    <form
      className="grid grid-cols-12 gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!count) return onError("Enter the quantity to write off");
        void run(async () => {
          await api.writeOff(lot.id, { quantity: count, happenedOn: on, reason });
          setReason("");
          onDone(`${count} written off: ${reason}.`);
        });
      }}
    >
      <Field id="off-quantity" label="Quantity" className="sm:col-span-4 col-span-12" hint={`Of ${lot.quantityOnHand}.`}>
        <input id="off-quantity" type="text" inputMode="numeric" className="form-control" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
      </Field>
      <Field id="off-on" label="Date" className="sm:col-span-8 col-span-12">
        <DateField id="off-on" value={on} onChange={setOn} />
      </Field>
      <Field id="off-reason" label="Reason" className="col-span-12" hint="Saved in the batch history. The cost is removed from inventory with it.">
        <input id="off-reason" type="text" className="form-control" value={reason} placeholder="Water damage in storage" onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div className="col-span-12">
        <button type="submit" className="ti-btn ti-btn-danger-full !mb-0" disabled={busy || !count || !reason.trim()}>
          {busy ? <Working label="Saving" /> : "Write off"}
        </button>
      </div>
    </form>
  );
}

function SellForm({
  lot,
  locations,
  seesCost,
  onError,
  onDone,
}: FormProps & { readonly locations: readonly Location[]; readonly seesCost: boolean }): React.ReactElement {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState("");
  const [quantity, setQuantity] = useState(String(lot.quantityOnHand));
  const [on, setOn] = useState(singaporeToday());
  const [placeId, setPlaceId] = useState(lot.locationId ?? "");
  const { busy, run } = useAction(onError);
  const count = wholeNumber(quantity);

  useEffect(() => {
    void api.accounts().then((result) => {
      const company = result.accounts.filter((account) => account.ownerKind === "company" && account.isActive && account.kind === "bank");
      setAccounts(company);
      if (company[0]) setAccountId(company[0].id);
    });
  }, []);

  return (
    <form
      className="grid grid-cols-12 gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!count) return onError("Enter the quantity the company is buying");
        void run(async () => {
          const result = await api.sellToCompany({
            lotId: lot.id,
            quantity: count,
            issuedOn: on,
            paidFromAccountId: accountId,
            locationId: placeId || null,
          });
          onDone(`${count} now belong to the company. Payment voucher ${result.voucherNumber} for ${formatMoney(result.amountCents)} has been raised to ${lot.ownerName}.`);
        });
      }}
    >
      <div className="col-span-12">
        <p className="text-[0.813rem] mb-0">
          The company buys this from {lot.ownerName} at their original cost
          {seesCost && lot.averageCostCents ? `, ${formatMoney(Math.round(lot.averageCostCents))} each` : ""}. A payment
          voucher is raised to them and closes when the bank payment is matched.
        </p>
      </div>
      <Field id="sell-quantity" label="Quantity" className="sm:col-span-4 col-span-12" hint={`Of ${lot.quantityOnHand}.`}>
        <input id="sell-quantity" type="text" inputMode="numeric" className="form-control" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
      </Field>
      <Field id="sell-account" label="Paid from" className="sm:col-span-8 col-span-12">
        <SelectField
          id="sell-account"
          value={accountId}
          placeholder="Select the company account"
          options={accounts.map((account) => ({ value: account.id, label: account.name }))}
          onChange={setAccountId}
        />
      </Field>
      <Field id="sell-on" label="Date" className="sm:col-span-5 col-span-12">
        <DateField id="sell-on" value={on} onChange={setOn} />
      </Field>
      <Field id="sell-place" label="Inventory location" className="sm:col-span-7 col-span-12">
        <SelectField
          id="sell-place"
          value={placeId}
          placeholder="Select a location"
          options={locations.filter((place) => place.isActive).map((place) => ({ value: place.id, label: place.name }))}
          onChange={setPlaceId}
        />
      </Field>
      <div className="col-span-12">
        <SubmitButton busy={busy} disabled={!count || !accountId}>
          Sell to company
        </SubmitButton>
      </div>
    </form>
  );
}

const GRADERS = ["PSA", "BGS", "CGC", "SGC", "TAG", "ACE"] as const;

/** The form's own words for the fields, for the sentence that says what to check. */
const SLAB_WORDS: Readonly<Record<string, string>> = {
  certNo: "the certificate number",
  grader: "the grader",
  grade: "the grade",
  gradeDescription: "the grade description",
  cardName: "the card",
  setName: "the set",
  yearReleased: "the year",
  cardNumber: "the card number",
  language: "the language",
  rarity: "the rarity",
};

function SlabForm({ lot, onError, onDone }: FormProps): React.ReactElement {
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({
    cardName: "",
    certNo: "",
    grader: "",
    grade: "",
    gradeDescription: "",
    setName: "",
    cardNumber: "",
    yearReleased: "",
    era: "",
    language: "",
    rarity: "",
    labelText: "",
    sellingPrice: "",
  });
  const [priceAge, setPriceAge] = useState<string | null>(null);
  const { busy, run } = useAction(onError);
  const canRead = useReading();
  const [reading, setReading] = useState(false);
  const [readNote, setReadNote] = useState<string | null>(null);
  /** The photo the label was read from, kept with the slab on Save details. */
  const [readPhoto, setReadPhoto] = useState<{ contentType: string; data: string } | undefined>();
  /** Where the cert number is on the photo, and whether to cover it (D, 9 October 2026). */
  const [certBox, setCertBox] = useState<CertBox | null>(null);
  const [hideCert, setHideCert] = useState(false);
  /** The slab's own photo as saved; null when it has none yet. */
  const [savedPhotoId, setSavedPhotoId] = useState<string | null>(lot.kind === "slab" ? (lot.photoId ?? null) : null);
  /** Dragging on the photo cuts it to the drag, rather than marking the number. */
  const [cutting, setCutting] = useState(false);

  /** The label, read off a photograph the person picks. Fills what it could read. */
  async function readLabel(file: File): Promise<void> {
    setReading(true);
    setReadNote(null);
    try {
      const picked = await fileForReading(file);
      const { reading: got } = await api.readSlab({ contentType: picked.contentType, data: picked.data });
      const photo = await cropToSlab(file, got.slabBox).catch(() => undefined);
      setReadPhoto(photo);
      // The new photo's own cert position; the old one belonged to the old photo.
      if (photo) {
        const found = boxInCrop(got.slabBox, got.certBox);
        setCertBox(found);
        // Covered unless somebody takes it off (D, 10 October 2026: "auto-hide the cert number").
        setHideCert(found !== null);
      }
      setForm((was) => ({
        ...was,
        cardName: got.cardName ?? was.cardName,
        certNo: got.certNo ?? was.certNo,
        grader: got.grader ?? was.grader,
        grade: got.grade ?? was.grade,
        gradeDescription: got.gradeDescription ?? was.gradeDescription,
        era: got.yearRegionEra ?? was.era,
        labelText: got.labelText ?? was.labelText,
        setName: got.setName ?? was.setName,
        cardNumber: got.cardNumber ?? was.cardNumber,
        yearReleased: got.yearReleased ? String(got.yearReleased) : was.yearReleased,
        language: got.language ?? was.language,
        rarity: got.rarity ?? was.rarity,
      }));
      setReadNote(
        unsureSentence(got.unsure, SLAB_WORDS) ?? "Read from the image. Check the details, then save.",
      );
    } catch (problem) {
      onError(problem instanceof ApiError ? problem.message : "Could not read the image");
    } finally {
      setReading(false);
    }
  }

  function fill(slab: SlabDetails | null): void {
    setForm({
      cardName: slab?.cardName ?? lot.slabName ?? "",
      certNo: slab?.certNo ?? "",
      grader: slab?.grader ?? "",
      grade: slab?.grade ?? "",
      gradeDescription: slab?.gradeDescription ?? "",
      era: slab?.era ?? "",
      labelText: slab?.labelText ?? "",
      setName: slab?.setName ?? "",
      cardNumber: slab?.cardNumber ?? "",
      yearReleased: slab?.yearReleased ? String(slab.yearReleased) : "",
      language: slab?.language ?? "",
      rarity: slab?.rarity ?? "",
      sellingPrice: slab?.sellingPriceCents === null || slab?.sellingPriceCents === undefined ? "" : formatMoney(slab.sellingPriceCents).replace(/,/g, ""),
    });
    setPriceAge(slab?.priceSetOn ?? null);
    setCertBox(slab?.certBox ?? null);
    setHideCert(slab?.hideCert ?? false);
    setLoaded(true);
  }

  useEffect(() => {
    void api.slab(lot.id).then((result) => fill(result.slab));
  }, [lot.id]);

  if (!loaded) return <Loading />;

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: event.target.value });
  const missing = [
    !form.cardName.trim() && "the card name",
    !form.certNo.trim() && "the certificate number",
    !form.grader.trim() && "the grader",
    !form.grade.trim() && "the grade",
  ].filter(Boolean);

  return (
    <form
      className="grid grid-cols-12 gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        const year = form.yearReleased.trim() ? Number(form.yearReleased) : null;
        void run(async () => {
          const { slab } = await api.saveSlab(lot.id, {
            cardName: form.cardName,
            certNo: form.certNo,
            grader: form.grader,
            grade: form.grade,
            // Sent every time, or a save would empty them (D, 8 October 2026: GEM MT went missing).
            gradeDescription: form.gradeDescription,
            era: form.era,
            labelText: form.labelText,
            setName: form.setName,
            cardNumber: form.cardNumber,
            yearReleased: year,
            language: form.language,
            rarity: form.rarity,
            sellingPrice: form.sellingPrice.trim() || null,
            certBox,
            hideCert: hideCert && certBox !== null,
          });
          if (readPhoto) {
            const stored = await api.setSlabPhoto(lot.id, readPhoto).catch(() => undefined);
            if (stored) setSavedPhotoId(stored.photo.id);
            setReadPhoto(undefined);
          }
          fill(slab);
          onDone(missing.length === 0 ? "Saved. This graded card record is complete." : "Saved. The record stays in Incomplete records until the remaining details are added.");
        });
      }}
    >
      {canRead ? (
        <div className="col-span-12 flex flex-wrap items-center gap-3">
          <ReadFileButton id="slab-read" label="Read the label from a photo" accept="image/*" busy={reading} onFile={(file) => void readLabel(file)} />
          <span className="text-[0.75rem] text-[#8c9097] dark:text-white/50">
            Fills in the details from the label. Nothing is saved until you select Save details.
          </span>
        </div>
      ) : null}
      {readNote ? (
        <div className="col-span-12">
          <Alert tone="info" onClose={() => setReadNote(null)}>{readNote}</Alert>
        </div>
      ) : null}
      {missing.length > 0 ? (
        <div className="col-span-12">
          <Alert tone="warning">To complete this record, add {missing.join(", ").replace(/, ([^,]*)$/, " and $1")}.</Alert>
        </div>
      ) : null}
      <Field id="slab-card" label="Card name" className="col-span-12">
        <input id="slab-card" type="text" className="form-control" value={form.cardName} placeholder="Charizard ex SAR" onChange={set("cardName")} />
      </Field>
      <Field id="slab-cert" label="Certificate number" className="sm:col-span-5 col-span-12">
        <input id="slab-cert" type="text" inputMode="numeric" className="form-control" value={form.certNo} onChange={set("certNo")} />
      </Field>
      <Field id="slab-grader" label="Grader" className="sm:col-span-4 col-span-6">
        <SelectField
          id="slab-grader"
          value={form.grader.toUpperCase()}
          placeholder="Select"
          options={[
            ...GRADERS.map((grader) => ({ value: grader, label: grader })),
            ...(form.grader && !(GRADERS as readonly string[]).includes(form.grader.toUpperCase())
              ? [{ value: form.grader.toUpperCase(), label: form.grader.toUpperCase() }]
              : []),
          ]}
          onChange={(grader) => setForm({ ...form, grader })}
        />
      </Field>
      <Field id="slab-grade" label="Grade" className="sm:col-span-3 col-span-6">
        <input id="slab-grade" type="text" className="form-control" value={form.grade} placeholder="10" onChange={set("grade")} />
      </Field>
      <Field id="slab-set" label="Card set" className="sm:col-span-7 col-span-12">
        <input id="slab-set" type="text" className="form-control" value={form.setName} placeholder="Ascended Heroes" onChange={set("setName")} />
      </Field>
      <Field id="slab-number" label="Card number" className="sm:col-span-5 col-span-12">
        <input id="slab-number" type="text" className="form-control" value={form.cardNumber} placeholder="199/165" onChange={set("cardNumber")} />
      </Field>
      <Field id="slab-year" label="Year" className="sm:col-span-4 col-span-6">
        <input id="slab-year" type="text" inputMode="numeric" className="form-control" value={form.yearReleased} placeholder="2024" onChange={set("yearReleased")} />
      </Field>
      <Field id="slab-language" label="Language" className="sm:col-span-4 col-span-6">
        <input id="slab-language" type="text" className="form-control" value={form.language} placeholder="EN" onChange={set("language")} />
      </Field>
      <Field id="slab-rarity" label="Rarity" className="sm:col-span-4 col-span-12">
        <input id="slab-rarity" type="text" className="form-control" value={form.rarity} placeholder="SAR" onChange={set("rarity")} />
      </Field>
      <Field id="slab-grade-words" label="Grade words" className="sm:col-span-4 col-span-12">
        <input id="slab-grade-words" type="text" className="form-control" value={form.gradeDescription} placeholder="GEM MT" onChange={set("gradeDescription")} />
      </Field>
      <Field id="slab-era" label="Year, region and era" className="sm:col-span-8 col-span-12">
        <input id="slab-era" type="text" className="form-control" value={form.era} placeholder="2019 P.M. JPN. SUN & MOON" onChange={set("era")} />
      </Field>
      <Field id="slab-label" label="Full label" className="col-span-12" hint="Everything printed on the label apart from the barcode, as read from the photo.">
        <textarea id="slab-label" rows={2} className="form-control" value={form.labelText} onChange={set("labelText")} />
      </Field>
      {readPhoto || savedPhotoId ? (
        <div className="col-span-12">
          <label className="form-label">Photo</label>
          <div>
            <CertMarker
              src={readPhoto ? `data:${readPhoto.contentType};base64,${readPhoto.data}` : `/api/photos/${savedPhotoId}`}
              box={certBox}
              hidden={hideCert}
              cutting={cutting}
              onBox={(box) => {
                if (cutting) {
                  // Cut to the drag: kept on Save details, with the cover moved to match.
                  const src = readPhoto ? `data:${readPhoto.contentType};base64,${readPhoto.data}` : `/api/photos/${savedPhotoId}`;
                  void cutPhoto(src, box)
                    .then((cut) => {
                      setReadPhoto(cut);
                      setCertBox((was) => boxWithin(box, was));
                      setCutting(false);
                    })
                    .catch(() => onError("The photo could not be cut"));
                  return;
                }
                setCertBox(box);
                setHideCert(true);
              }}
            />
          </div>
          <button
            type="button"
            className={`ti-btn ${cutting ? "ti-btn-success-full" : "ti-btn-light"} !py-1 !px-3 !text-[0.75rem] !mb-0 mt-2`}
            onClick={() => setCutting(!cutting)}
          >
            <i className="ri-crop-line" /> {cutting ? "Drag across the slab to cut the photo to it" : "Cut the photo"}
          </button>
          <div className="form-check mt-2">
            <input
              id="slab-hide-cert"
              type="checkbox"
              className="form-check-input"
              checked={hideCert && certBox !== null}
              disabled={certBox === null}
              onChange={(event) => setHideCert(event.target.checked)}
            />
            <label htmlFor="slab-hide-cert" className="form-check-label">
              Hide the cert number on the photo
            </label>
          </div>
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
            {certBox === null
              ? "Drag across the cert number on the photo to mark where it is."
              : "Covered on Inventory, and on the photo when it is opened or saved. The photo itself is kept as taken, so it can be shown again. Drag across the photo to move the cover. A cut photo is kept on Save details."}
          </p>
        </div>
      ) : null}
      <Field
        id="slab-price"
        label="Selling price"
        className="sm:col-span-6 col-span-12"
        hint={priceAge ? `Price last updated ${priceAge}. A new price is dated today.` : "Dated today when saved."}
      >
        <input id="slab-price" type="text" inputMode="decimal" className="form-control" value={form.sellingPrice} placeholder="550.00" onChange={set("sellingPrice")} />
      </Field>
      <div className="col-span-12">
        <SubmitButton busy={busy} disabled={false}>
          Save details
        </SubmitButton>
      </div>
    </form>
  );
}
